import React, { useState, useRef, useEffect } from "react";

import {
  Avatar,
  Box,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  ShareOutlined as ShareIcon,
  Verified as VerifiedIcon,
  EmojiEvents as TrophyIcon,
  Bolt as BoltIcon,
  Autorenew as AutoIcon,
} from "@mui/icons-material";

import { toast } from "react-toastify";

import { colors } from "../../../../../theme";
import { CommonButton, CommonChip } from "../../../../Components/ui";

import {
  THREE_SLOT_CONFIG,
  SYMBOLS,
  getRandomSymbol,
} from "./threeSlotConfig";
// import slotInstance from "../../../../Service/slotInstance";
// import web3 from "../../../../Service/web3";

const CURRENCY = "BET";

const ThreeSlotMachineDetailCard = ({
  lotteryData,
  account,
  balance,
  win,
  setWin,
}) => {
  const [reels, setReels] = useState(
    THREE_SLOT_CONFIG.initialReels.map((id) =>
      SYMBOLS.find((symbol) => symbol.id === id)
    )
  );

  const [isSpinning, setIsSpinning] = useState(false);

  const spinningReelsRef = useRef(
    Array(THREE_SLOT_CONFIG.reels).fill(false)
  );

  const [result, setResult] = useState(null);
  const [betInput, setBetInput] = useState("");
  const [hasBetError, setHasBetError] = useState(false);
  const [isAutoSpinning, setIsAutoSpinning] = useState(false);

  const [jackpots, setJackpots] = useState(() => ({
    mini: parseFloat(lotteryData.jackpots.mini.amount) || 0,
    major: parseFloat(lotteryData.jackpots.major.amount) || 0,
    mega: parseFloat(lotteryData.jackpots.mega.amount) || 0,
  }));

  const getContributionAmount = (jackpot) => {
    return (
      parseFloat(
        jackpot.contributionPerSpin ?? jackpot.contributionPercent
      ) || 0
    );
  };

  const getWinningJackpot = (finalReels) => {
    const firstSymbol = finalReels[0]?.id;

    const isSameSymbol =
      finalReels.length === 3 &&
      finalReels[0]?.id === finalReels[1]?.id &&
      finalReels[1]?.id === finalReels[2]?.id;

    if (!isSameSymbol) {
      return null;
    }

    if (firstSymbol === "cherry") {
      return {
        type: "mini",
        label: "Mini",
      };
    }

    if (firstSymbol === "diamond") {
      return {
        type: "major",
        label: "Major",
      };
    }

    if (firstSymbol === "seven") {
      return {
        type: "mega",
        label: "Mega",
      };
    }

    return null;
  };

  const handleSpin = () => {
    const betVal = parseFloat(betInput);

    if (isNaN(betVal) || betVal <= 0) {
      toast.error("Please enter a valid bet amount greater than 0", {
        toastId: "bet-error",
      });

      setHasBetError(true);
      return;
    }

    setHasBetError(false);

    if (isSpinning) {
      return;
    }

    setIsSpinning(true);
    setResult(null);

    // If on-chain contract and connected account are available, dispatch spin3Reel transaction
    /*
    if (slotInstance?.methods?.spin3Reel && account && lotteryData?.lotteryId) {
      try {
        const betWei = web3?.utils?.toWei
          ? web3.utils.toWei(String(betVal), "ether")
          : "1000000000000000000";
        slotInstance.methods
          .spin3Reel(lotteryData.lotteryId, betWei)
          .send({ from: account })
          .catch((err) => {
            console.warn("On-chain spin transaction note:", err?.message || err);
          });
      } catch (e) {
        console.warn("Could not dispatch on-chain spin3Reel:", e);
      }
    }
    */

    spinningReelsRef.current = Array(
      THREE_SLOT_CONFIG.reels
    ).fill(true);

    const finalReels = Array.from(
      { length: THREE_SLOT_CONFIG.reels },
      () => getRandomSymbol()
    );

    const interval = setInterval(() => {
      setReels((previousReels) =>
        previousReels.map((reel, index) =>
          spinningReelsRef.current[index]
            ? getRandomSymbol()
            : reel
        )
      );
    }, 100);

    setTimeout(() => {
      spinningReelsRef.current[0] = false;

      setReels((previousReels) => [
        finalReels[0],
        previousReels[1],
        previousReels[2],
      ]);
    }, 800);

    setTimeout(() => {
      spinningReelsRef.current[1] = false;

      setReels((previousReels) => [
        finalReels[0],
        finalReels[1],
        previousReels[2],
      ]);
    }, 1400);

    setTimeout(() => {
      spinningReelsRef.current[2] = false;

      setReels(finalReels);
      clearInterval(interval);
      setIsSpinning(false);

      const winningJackpot = getWinningJackpot(finalReels);

      if (winningJackpot) {
        const winAmount = jackpots[winningJackpot.type];

        setWin((previousWin) => {
          const previousWinValue = parseFloat(previousWin) || 0;

          return (previousWinValue + winAmount).toFixed(2);
        });

        setResult({
          type: "win",
          winType: winningJackpot.type,
          label: winningJackpot.label,
          symbol: finalReels[0],
          amount: winAmount.toFixed(2),
        });

        toast.success(
          `${winningJackpot.label} Jackpot! You won ${winAmount.toFixed(
            2
          )} ${CURRENCY}.`,
          {
            toastId: "jackpot-win",
          }
        );

        setIsAutoSpinning((previousAutoSpin) => {
          if (previousAutoSpin) {
            toast.success("Winner! Auto Spin Paused.", {
              toastId: "auto-pause",
            });

            return false;
          }

          return previousAutoSpin;
        });
      } else {
        setResult({
          type: "lose",
          winType: null,
          label: null,
          symbol: null,
          amount: "0.00",
        });
      }
    }, 2000);
  };

  const handleSpinRef = useRef(handleSpin);

  useEffect(() => {
    handleSpinRef.current = handleSpin;
  }, [handleSpin]);

  useEffect(() => {
    let timer;

    if (isAutoSpinning && !isSpinning) {
      timer = setTimeout(() => {
        if (isAutoSpinning) {
          handleSpinRef.current();
        }
      }, 1500);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [isAutoSpinning, isSpinning]);

  const handleAutoSpin = () => {
    if (isAutoSpinning) {
      setIsAutoSpinning(false);

      toast.info("Auto Spin Stopped", {
        toastId: "auto-spin",
        autoClose: 2000,
      });

      return;
    }

    const betVal = parseFloat(betInput);

    if (isNaN(betVal) || betVal <= 0) {
      toast.error("Please enter a valid bet amount greater than 0", {
        toastId: "bet-error",
      });

      setHasBetError(true);
      return;
    }

    setIsAutoSpinning(true);

    toast.success("Auto Spin Started!", {
      toastId: "auto-spin",
      autoClose: 2000,
    });

    if (!isSpinning) {
      handleSpin();
    }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/three-slotlottery/${lotteryData.lotteryId}`;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);

      toast.success("Slot machine link copied!");
    } else {
      toast.error("Unable to copy the link.");
    }
  };

  return (
    <Box
      className={`slot-machine-card ${
        isSpinning ? "is-spinning" : ""
      }`}
      sx={{ p: { xs: 2, sm: 3 } }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: colors.primary.lighter,
                color: colors.primary.main,
                fontWeight: 700,
              }}
            >
              A
            </Avatar>

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                  }}
                >
                  {lotteryData.creator}
                </Typography>

                <VerifiedIcon
                  sx={{
                    fontSize: 14,
                    color: colors.primary.main,
                  }}
                />
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[400],
                  fontFamily: "monospace",
                }}
              >
                Machine #{lotteryData.lotteryId}
              </Typography>
            </Box>
          </Stack>

          <Tooltip title="Share Slot Machine" arrow>
            <IconButton
              size="small"
              onClick={handleShare}
              sx={{
                color: colors.slate[400],
                p: 0.5,
              }}
            >
              <ShareIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box
        sx={{
          py: 1.5,
          borderTop: `1px solid ${colors.slate[100]}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[500],
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Balance
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                color: colors.slate[900],
              }}
            >
              {balance}{" "}
              <span
                style={{
                  fontSize: "0.75rem",
                  color: colors.slate[500],
                }}
              >
                {CURRENCY}
              </span>
            </Typography>
          </Box>

          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[500],
                fontWeight: 600,
                textTransform: "uppercase",
                display: "block",
                mb: 0.25
              }}
            >
              Lottery
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: colors.slate[800],
              }}
            >
              {lotteryData.name}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, textAlign: "right" }}>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[500],
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Win
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                color: colors.emerald[600],
              }}
            >
              {win}{" "}
              <span
                style={{
                  fontSize: "0.75rem",
                  color: colors.slate[500],
                }}
              >
                {CURRENCY}
              </span>
            </Typography>
          </Box>
        </Stack>
      </Box>



        <Box
          sx={{
            py: 1.5,
            borderTop: `1px solid ${colors.slate[100]}`,
            borderBottom: `1px solid ${colors.slate[100]}`,
            mb: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              sx={{
                flex: 1,
                textAlign: "center",
                borderRight: `1px solid ${colors.slate[100]}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[500],
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                MINI JACKPOT
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: colors.blue[600],
                }}
              >
                {jackpots.mini.toFixed(2)}{" "}
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: colors.blue[400],
                  }}
                >
                  {CURRENCY}
                </span>
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: colors.slate[400],
                  mt: 0.25,
                }}
              >
                +
                {getContributionAmount(
                  lotteryData.jackpots.mini
                ).toFixed(3)}
                {" "}{CURRENCY} / spin
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                textAlign: "center",
                borderRight: `1px solid ${colors.slate[100]}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[500],
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                MAJOR JACKPOT
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: colors.blue[600],
                }}
              >
                {jackpots.major.toFixed(2)}{" "}
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: colors.blue[400],
                  }}
                >
                  {CURRENCY}
                </span>
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: colors.slate[400],
                  mt: 0.25,
                }}
              >
                +
                {getContributionAmount(
                  lotteryData.jackpots.major
                ).toFixed(3)}
                {" "}{CURRENCY} / spin
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                textAlign: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[500],
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                MEGA JACKPOT
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: colors.blue[600],
                }}
              >
                {jackpots.mega.toFixed(2)}{" "}
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: colors.blue[400],
                  }}
                >
                  {CURRENCY}
                </span>
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: colors.slate[400],
                  mt: 0.25,
                }}
              >
                +
                {getContributionAmount(
                  lotteryData.jackpots.mega
                ).toFixed(3)}
                {" "}{CURRENCY} / spin
              </Typography>
            </Box>
          </Stack>
        </Box>

      <Box className="slot-machine-stage">
        <Box className="slot-machine-top" sx={{ display: "none" }} />

        <Box className="slot-reels-wrapper">
          <Box className="slot-payline" />

          {reels.map((reel, index) => {
            const isReelSpinning =
              spinningReelsRef.current[index];

            const symIndex = SYMBOLS.findIndex(
              (symbol) => symbol.id === reel.id
            );

            const topSym = isReelSpinning
              ? getRandomSymbol().symbol
              : SYMBOLS[
                  (symIndex + 1) % SYMBOLS.length
                ].symbol;

            const bottomSym = isReelSpinning
              ? getRandomSymbol().symbol
              : SYMBOLS[
                  (symIndex + SYMBOLS.length - 1) %
                    SYMBOLS.length
                ].symbol;

            return (
              <Box
                key={`${reel.id}-${index}`}
                className={`slot-reel ${
                  isReelSpinning
                    ? "reel-spinning"
                    : ""
                }`}
              >
                <Typography
                  className="slot-symbol slot-symbol-top"
                  sx={{
                    fontSize:
                      topSym === "BAR"
                        ? "1.1rem"
                        : undefined,
                  }}
                >
                  {topSym}
                </Typography>

                <Typography
                  className="slot-symbol slot-symbol-main"
                  sx={{
                    fontSize:
                      reel.symbol === "BAR"
                        ? "1.3rem"
                        : undefined,
                    fontWeight:
                      reel.symbol === "BAR"
                        ? 900
                        : undefined,
                  }}
                >
                  {reel.symbol}
                </Typography>

                <Typography
                  className="slot-symbol slot-symbol-bottom"
                  sx={{
                    fontSize:
                      bottomSym === "BAR"
                        ? "1.1rem"
                        : undefined,
                  }}
                >
                  {bottomSym}
                </Typography>
              </Box>
            );
          })}
        </Box>


      </Box>

      <Box sx={{ mb: 1.5 }}>
        {result && (
          <Box
            className={`slot-result ${
              result.type === "win"
                ? "slot-result-jackpot"
                : "slot-result-loss"
            }`}
          >
            {result.type === "lose" ? (
              <BoltIcon
                sx={{
                  fontSize: 22,
                  color: colors.slate[400],
                }}
              />
            ) : (
              <TrophyIcon
                sx={{
                  fontSize: 22,
                  color: colors.amber[500],
                }}
              />
            )}

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color:
                    result.type === "lose"
                      ? colors.slate[700]
                      : colors.amber[800],
                }}
              >
                {result.type === "win"
                  ? `${result.label.toUpperCase()} JACKPOT!`
                  : "Better Luck Next Spin"}
              </Typography>

              {result.type === "win" && (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.slate[500],
                    fontWeight: 600,
                  }}
                >
                  Won {result.amount} {CURRENCY}
                </Typography>
              )}
            </Box>

            <CommonChip
              label={
                result.type === "lose"
                  ? "MISSED"
                  : `${result.label.toUpperCase()} WIN`
              }
              variant={
                result.type === "lose"
                  ? "neutral"
                  : "jackpot"
              }
              size="small"
              sx={{
                fontWeight: 800,
              }}
            />
          </Box>
        )}
      </Box>

      <Box>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            height: "54px",
          }}
        >
          <Box
            sx={{
              flex: 1.2,
              border: `1px solid ${
                hasBetError
                  ? colors.red[500]
                  : colors.slate[200]
              }`,
              borderRadius: "10px",
              p: "6px 10px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              bgcolor: hasBetError
                ? "rgba(239, 68, 68, 0.05)"
                : colors.slate[50],
              transition: "all 0.2s ease",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: hasBetError
                  ? colors.red[600]
                  : colors.slate[500],
                fontWeight: 700,
                fontSize: "0.6rem",
                lineHeight: 1.2,
              }}
            >
              BET AMOUNT
            </Typography>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <InputBase
                type="number"
                placeholder="0.00"
                value={betInput}
                onChange={(e) => {
                  setBetInput(e.target.value);

                  if (hasBetError) {
                    setHasBetError(false);
                  }
                }}
                sx={{
                  width: "100%",
                  fontWeight: 800,
                  fontSize: "1rem",
                  color: colors.slate[900],
                  p: 0,

                  "& input, & input:focus, & input:active, & input:focus-visible":
                    {
                      outline: "none !important",
                      border: "none !important",
                      boxShadow: "none !important",
                      padding: "4px 0",
                    },

                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    {
                      display: "none",
                    },

                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                }}
              />

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: colors.slate[400],
                }}
              >
                {CURRENCY}
              </Typography>
            </Stack>
          </Box>

          <CommonButton
            variant="primary"
            size="small"
            startIcon={<BoltIcon />}
            onClick={handleSpin}
            disabled={isSpinning}
            sx={{
              flex: 1,
              borderRadius: "10px",
              fontWeight: 800,
              height: "100%",
              px: "10px",
              bgcolor: colors.blue[600],

              "&:hover": {
                bgcolor: colors.blue[700],
              },
            }}
          >
            {isSpinning ? "..." : "SPIN"}
          </CommonButton>

          <CommonButton
            variant="neutral"
            size="small"
            startIcon={
              isAutoSpinning ? null : <AutoIcon />
            }
            onClick={handleAutoSpin}
            sx={{
              flex: 0.8,
              borderRadius: "10px",
              fontWeight: 800,
              height: "100%",
              px: "10px",
              color: isAutoSpinning
                ? colors.red[600]
                : colors.blue[600],
              bgcolor: isAutoSpinning
                ? colors.red[50]
                : "#ffffff",

              border: `1px solid ${
                isAutoSpinning
                  ? colors.red[200]
                  : colors.slate[200]
              }`,

              "&:hover": {
                bgcolor: isAutoSpinning
                  ? colors.red[100]
                  : colors.slate[50],
              },
            }}
          >
            {isAutoSpinning ? "STOP" : "AUTO"}
          </CommonButton>
        </Stack>
      </Box>
    </Box>
  );
};

export default ThreeSlotMachineDetailCard;